import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";

/**
 * Custom hook to return a debounced value.
 * The value will only update after the specified delay has passed since the last change.
 */
export function useDebouncedValue<T>(value: T, delay = 500): T {
	const [debouncedValue, setDebouncedValue] = useState(value);

	const debouncedSetter = useMemo(() => debounce((val: T) => setDebouncedValue(val), delay), [delay]);

	useEffect(() => {
		debouncedSetter(value);

		return () => {
			// Cleanup to cancel debounce if value changes quickly or component unmounts
			debouncedSetter.cancel();
		};
	}, [value, debouncedSetter]);

	return debouncedValue;
}
